"use client"

import { clx } from "@modules/common/components/ui"
import Image from "next/image"
import { ButtonHTMLAttributes, useEffect, useRef } from "react"

// Product image with a neutral placeholder box for the many accessories that
// don't have photos yet.
export function Thumb({
  src,
  alt,
  className,
}: {
  src: string | null
  alt: string
  className?: string
}) {
  return (
    <div
      className={clx(
        "relative shrink-0 rounded-md bg-gray-100 border border-gray-200 overflow-hidden",
        className
      )}
    >
      {src ? (
        <Image
          src={src}
          alt={alt}
          className="object-contain object-center p-1"
          sizes="96px"
          fill
        />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center text-gray-300"
          aria-hidden="true"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="5"
              width="18"
              height="14"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <circle cx="9" cy="10" r="1.5" fill="currentColor" />
            <path
              d="m5 17 4.5-4 3 2.5L16 12l3 3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </div>
  )
}

type AmberButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  isLoading?: boolean
  size?: "large" | "small"
  // Focus this button when the step mounts so it reads as pre-selected and
  // Enter confirms the default choice.
  autoFocusOnMount?: boolean
}

// The big amber primary CTA every wizard step leads with.
export function AmberButton({
  className,
  isLoading,
  disabled,
  size = "large",
  autoFocusOnMount,
  children,
  ...props
}: AmberButtonProps) {
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!autoFocusOnMount) {
      return
    }
    // Deferred so it wins over the Headless UI dialog focus trap, which
    // focuses the first tabbable element when the modal opens.
    const id = window.setTimeout(() => ref.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [autoFocusOnMount])

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || isLoading}
      className={clx(
        "inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 font-semibold text-white transition-colors hover:bg-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60",
        size === "large" ? "h-12 px-6 text-base" : "h-10 px-4 text-sm",
        className
      )}
      {...props}
    >
      {isLoading ? "Adding..." : children}
    </button>
  )
}

// The small plain-text decline link under each step's primary CTA.
export function DeclineLink({
  onClick,
  disabled,
  children,
  "data-testid": dataTestId,
}: {
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
  "data-testid"?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      className="self-center text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 disabled:opacity-50"
    >
      {children}
    </button>
  )
}

// "Added" confirmation shown on choices that are already in the cart.
export function AddedCheck({ className }: { className?: string }) {
  return (
    <span
      className={clx(
        "inline-flex items-center gap-x-1 text-sm font-medium text-green-700",
        className
      )}
    >
      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M4 10.5 8.5 15 16 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Added
    </span>
  )
}

// Selectable choice card. Renders as a button, so never nest buttons inside.
export function ChoiceCard({
  selected,
  added,
  onSelect,
  disabled,
  className,
  children,
  "data-testid": dataTestId,
}: {
  selected: boolean
  added?: boolean
  onSelect: () => void
  disabled?: boolean
  className?: string
  children: React.ReactNode
  "data-testid"?: string
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled || added}
      aria-pressed={selected}
      data-testid={dataTestId}
      className={clx(
        "w-full rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
        selected && !added
          ? "border-amber-500 ring-1 ring-amber-500 bg-amber-50"
          : "border-gray-200 bg-white hover:border-gray-300",
        added && "opacity-70",
        className
      )}
    >
      {children}
    </button>
  )
}

// Progress dots across the top of the wizard.
export function ProgressDots({
  total,
  current,
}: {
  total: number
  current: number
}) {
  return (
    <div className="flex items-center gap-x-1.5" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={clx(
            "h-1.5 rounded-full transition-all",
            i === current
              ? "w-4 bg-amber-500"
              : i < current
              ? "w-1.5 bg-amber-500"
              : "w-1.5 bg-gray-200"
          )}
        />
      ))}
    </div>
  )
}
